import React from "react";
import { FaClock, FaEdit, FaEuroSign, FaSearch, FaTrash } from "react-icons/fa";

import { formatPrice, getGroupColorClass } from "../utils/groupesUtils";

export default function GroupesTable({
  rows,
  loading,
  searchTerm,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
  onEdit,
  onDelete,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-white/10 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Tableau des groupes
            </h2>

            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {rows.length.toLocaleString("fr-FR")} groupe
              {rows.length > 1 ? "s" : ""} affiché
              {rows.length > 1 ? "s" : ""}.
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-[minmax(240px,1fr)_180px] xl:min-w-[560px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Rechercher un groupe..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(event) => onStatusFilterChange(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="all">Tous</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-5 lg:p-6">
        <div className="max-h-[72vh] overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/60">
          <table className="min-w-[1040px] w-full text-sm">
            <thead className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900">
              <tr className="text-left">
                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Groupe
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Horaires
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Années de naissance
                </th>

                <th className="px-4 py-3 text-center font-bold text-slate-700 dark:text-slate-200">
                  Tarif
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Statut
                </th>

                <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-white/10">
              {loading ? (
                [...Array(8)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={6} className="px-4 py-3">
                      <div className="h-12 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-slate-500 dark:text-slate-400"
                  >
                    Aucun groupe ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                rows.map((groupe) => (
                  <tr
                    key={groupe.id}
                    className={[
                      "transition hover:bg-slate-50 dark:hover:bg-slate-900",
                      groupe.is_active === false
                        ? "bg-slate-50 text-slate-500 dark:bg-slate-900/60"
                        : "",
                    ].join(" ")}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span
                          className={[
                            "h-11 w-11 shrink-0 rounded-2xl ring-1 ring-slate-200 dark:ring-white/10",
                            getGroupColorClass(groupe.color_groupe),
                          ].join(" ")}
                        />

                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 dark:text-white">
                            {groupe.nom || "Sans nom"}
                          </p>

                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            ID {groupe.id}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <ScheduleLine value={groupe.horaire1} />
                        <ScheduleLine value={groupe.horaire2} muted />
                      </div>
                    </td>

                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                      {groupe.annees_naissance || "—"}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                        {formatPrice(groupe.tarif_groupe).replace(" €", "")}
                        <FaEuroSign />
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <StatusBadge active={groupe.is_active !== false} />
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit(groupe)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                          title="Modifier"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          onClick={() => onDelete(groupe.id)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700"
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
          <FaClock className="mr-2 inline text-slate-400" />
          Format conseillé pour les horaires :{" "}
          <strong>Mardi 17h00-18h00</strong>. Ce format permet un affichage
          propre sur la page publique.
        </div>
      </div>
    </div>
  );
}

function ScheduleLine({ value, muted = false }) {
  if (!value) {
    return (
      <span className="text-xs text-slate-400">
        {muted ? "Pas de second horaire" : "Horaire non renseigné"}
      </span>
    );
  }

  return (
    <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
      {value}
    </span>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={[
        "inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1",
        active
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      ].join(" ")}
    >
      {active ? "Actif" : "Inactif"}
    </span>
  );
}
